import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { categoryLabel } from "../lib/articleCategories";
import TierBadge from "./TierBadge";

function ArticleTile({ article }) {
  const tier = article.tier_required;
  return (
    <Link
      to={`/articles/${article.slug}`}
      className="block h-full group"
      data-testid={`home-article-${article.slug}`}
    >
      <Card className="overflow-hidden bg-card border-white/5 hover:border-emerald-500/30 transition-all h-full flex flex-col">
        <div
          className="h-44 bg-gradient-to-br from-emerald-500/20 to-transparent border-b border-white/5 relative shrink-0"
          style={
            article.cover_image
              ? { backgroundImage: `url(${article.cover_image})`, backgroundSize: "cover", backgroundPosition: "center" }
              : undefined
          }
        >
          {tier && tier !== "free" && (
            <span className="absolute top-3 right-3" data-testid={`article-premium-badge-${article.slug}`}>
              {/* Règle d'accès : tout abonné dès l'offre Essentiel lit les analyses premium */}
              <TierBadge tier="essentiel" locked={!!article.is_locked} blur />
            </span>
          )}
        </div>
        <div className="p-5 flex-1 flex flex-col">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{categoryLabel(article.category)}</div>
          <h3 className="mt-2 font-display font-bold text-lg leading-snug group-hover:text-emerald-300 line-clamp-2">
            {article.title}
          </h3>
          {article.excerpt && (
            <p className="mt-2 text-sm text-zinc-400 line-clamp-2 flex-1">{article.excerpt}</p>
          )}
          <div className="mt-3 text-xs text-zinc-500 pt-2 border-t border-white/5">
            {article.created_at ? new Date(article.created_at).toLocaleDateString("fr-FR") : ""}
          </div>
        </div>
      </Card>
    </Link>
  );
}

function ViewMoreTile() {
  return (
    <Link
      to="/articles"
      className="block h-full group"
      data-testid="home-articles-view-more-tile"
    >
      <Card className="overflow-hidden bg-gradient-to-br from-violet-500/[0.10] to-transparent border border-violet-500/30 hover:border-violet-500/60 transition-all h-full grid place-items-center p-6 text-center">
        <div>
          <div className="w-12 h-12 rounded-full bg-violet-500/15 border border-violet-500/40 grid place-items-center mx-auto">
            <ArrowRight className="w-5 h-5 text-violet-300" />
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-[0.25em] text-violet-400">Bibliothèque</div>
          <h3 className="mt-1.5 font-display font-bold text-lg">Toutes les analyses</h3>
          <p className="mt-2 text-xs text-zinc-500 max-w-[220px] mx-auto">La bibliothèque complète.</p>
        </div>
      </Card>
    </Link>
  );
}

export default function LatestArticlesCarousel() {
  const { data: allArticles = [] } = useQuery({
    queryKey: ["home-articles"],
    queryFn: async () => (await api.get("/articles")).data,
  });
  // Limite à 5 articles pour que la tuile « Voir plus » soit atteinte en 2 défilements
  // (vue initiale = [1,2,3] → scroll 1 = [2,3,4] → scroll 2 = [3,4,VoirPlus])
  const articles = allArticles.slice(0, 5);
  const scroller = useRef(null);
  const scroll = (dir) => {
    const el = scroller.current;
    if (!el) return;
    const firstTile = el.firstElementChild;
    const gap = 16;
    const step = firstTile ? firstTile.getBoundingClientRect().width + gap : 320;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  if (articles.length === 0) return null;

  return (
    <section
      className="mx-auto max-w-7xl px-6 pt-4 pb-2 sm:py-14"
      data-testid="home-articles-carousel"
    >
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-violet-400 mb-2">Dernières analyses</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl">Les marchés décryptés</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scroll(-1)}
            className="w-9 h-9 grid place-items-center rounded-lg border border-white/10 hover:bg-white/5 text-zinc-400"
            data-testid="home-articles-prev"
            aria-label="Précédent"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => scroll(1)}
            className="w-9 h-9 grid place-items-center rounded-lg border border-white/10 hover:bg-white/5 text-zinc-400"
            data-testid="home-articles-next"
            aria-label="Suivant"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div
        ref={scroller}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth no-scrollbar pb-2 -mx-6 px-6 scroll-px-6 sm:mx-0 sm:px-0 sm:scroll-px-0"
        style={{ scrollbarWidth: "none" }}
      >
        {articles.map((a) => (
          <div
            key={a.id}
            className="snap-start shrink-0 w-[82%] sm:w-[340px]"
          >
            <ArticleTile article={a} />
          </div>
        ))}
        <div
          className="snap-start shrink-0 w-[82%] sm:w-[340px]"
        >
          <ViewMoreTile />
        </div>
      </div>
    </section>
  );
}
